import React, { useEffect, useState } from 'react';
import Navbar from './Features/Navbar';
import hr from '../images/icons/hr.png';
import { Box, CircularProgress, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Footer from './Home/Footer';
import { Fade } from 'react-awesome-reveal';
import StrapiRichText from './StrapiRichText';
import { fetchStrapiSingleType } from '../services/strapiClient';
import { pickCmsText, pickCmsTitle } from '../utils/cmsContent';

const richTextSx = {
  fontSize: '20px',
  lineHeight: 1.8,
  fontFamily: 'Montserrat',
  color: '#2f3036',
  fontWeight: 500,
  '& p': { marginTop: 0, marginBottom: '1rem' },
  '& p:last-child': { marginBottom: 0 },
  '& ul, & ol': { marginBottom: '1rem', paddingLeft: '1.25rem' },
  '& ul ul': { marginTop: '0.5rem', marginBottom: '0.5rem' },
  '& li': { marginBottom: '0.35rem' },
  '& strong': { fontWeight: 700 },
  '& a': { color: '#1398aa' },
  '& u': { textDecoration: 'underline', fontStyle: 'italic' },
  '& h2': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '34px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& h3': {
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '28px',
    marginBottom: '1rem',
    marginTop: '1rem',
    fontWeight: 700,
    color: '#2f3036',
  },
  '& .service-callout': {
    borderStyle: 'solid',
    borderRadius: '20px',
    backgroundColor: '#EBEAFF',
    borderColor: '#EBEAFF',
    marginBottom: '3rem',
    padding: '20px',
    fontFamily: 'Montserrat',
    lineHeight: 1.8,
    fontSize: '20px',
    color: '#2f3036',
    fontWeight: 600,
  },
  '& .service-callout p': { marginTop: 0, marginBottom: '1rem' },
  '& .service-callout p:last-child': { marginBottom: 0 },
};

const defaultRowColors = {
  'Salaire Basique': '#FDDEF5',
  'Supplément en fonction du statut': '#FDDEF5',
  'Subvension soldat seul': '#FDDEF5',
  'Ramat Peïlout Aleph+': '#ECC6FF',
  'Ramat Peïlout Aleph': '#ECC6FF',
  'Ramat Peïlout Beth': '#ECC6FF',
  'Ramat Peïlout Guimel': '#ECC6FF',
  'Aide au logement': '#E2C3FF',
  "Maintenance de l'appartement": '#E2C3FF',
  'Olim Non-Mahal - Misrad Haklita': '#CBB5FE',
  'Olim Non-Mahal - Misrad Habinouy': '#CBB5FE',
};

function normalizeRich(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function unwrap(entry) {
  if (!entry || typeof entry !== 'object') return {};
  return entry.attributes && typeof entry.attributes === 'object' ? entry.attributes : entry;
}

function normalizeTableColumns(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => {
      const o = unwrap(x);
      return o.label != null ? String(o.label).trim() : '';
    })
    .filter(Boolean);
}

function normalizeTableRows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const o = unwrap(row);
      const title = o.title != null ? String(o.title).trim() : '';
      const firstCellColor = o.firstCellColor != null ? String(o.firstCellColor).trim() : '';
      const cells = Array.isArray(o.cells)
        ? o.cells.map((c) => {
            const cell = unwrap(c);
            return cell.value != null ? String(cell.value).trim() : '';
          })
        : [];
      return { title, firstCellColor, cells };
    })
    .filter((r) => r.title || r.cells.some(Boolean));
}

function hasStructuredTable(columns, rows) {
  return columns.length > 1 && rows.length > 0;
}

function renderSalaryTable(columns, rows) {
  return (
    <TableContainer component={Paper} style={{ marginBottom: '4rem' }}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((label, idx) => (
              <TableCell key={`th-${idx}`} style={{ backgroundColor: '#EBEAFF', fontWeight: 600, fontFamily: 'Montserrat' }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const bg = row.firstCellColor || defaultRowColors[row.title] || '#FFFFFF';
            const restCols = columns.slice(1);
            const nonEmptyCount = row.cells.filter((v) => String(v || '').trim() !== '').length;
            const isSingleValue = nonEmptyCount <= 1;
            return (
              <TableRow key={`row-${rowIndex}`}>
                <TableCell style={{ fontWeight: 600, fontFamily: 'Montserrat', backgroundColor: bg }}>{row.title}</TableCell>
                {isSingleValue ? (
                  <TableCell colSpan={restCols.length} style={{ fontFamily: 'Montserrat', textAlign: 'center' }}>
                    {row.cells.find((v) => String(v || '').trim() !== '') || ''}
                  </TableCell>
                ) : (
                  restCols.map((_, colIndex) => (
                    <TableCell key={`td-${rowIndex}-${colIndex}`} style={{ fontFamily: 'Montserrat', textAlign: 'center' }}>
                      {row.cells[colIndex] || ''}
                    </TableCell>
                  ))
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function AnnexeHayalBodedPageShell({
  strapiApiId = 'annexe-hayal-boded',
  defaultSectionLabel = 'Annexes',
  defaultTitle = 'Hayal Boded & Salaires',
  defaultTableOneTitle = 'Salaires soldats',
  defaultTableTwoTitle = 'Salaires soldats seuls',
  StaticFallback,
  fadeTriggerOnce = true,
}) {
  const [sectionLabel, setSectionLabel] = useState(defaultSectionLabel);
  const [title, setTitle] = useState(defaultTitle);
  const [tableOneTitle, setTableOneTitle] = useState(defaultTableOneTitle);
  const [tableTwoTitle, setTableTwoTitle] = useState(defaultTableTwoTitle);
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${title} | Tsahal Connection`;
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const attrs = await fetchStrapiSingleType(strapiApiId, { populate: 'populate=*' });
        if (cancelled) return;
        if (attrs) {
          setSectionLabel(pickCmsText(attrs.sectionLabel, defaultSectionLabel));
          setTitle(pickCmsTitle(attrs.title, defaultTitle));
          setTableOneTitle(pickCmsText(attrs.tableOneTitle, defaultTableOneTitle));
          setTableTwoTitle(pickCmsText(attrs.tableTwoTitle, defaultTableTwoTitle));
          setRemote(attrs);
        } else {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setTableOneTitle(defaultTableOneTitle);
          setTableTwoTitle(defaultTableTwoTitle);
          setRemote(null);
        }
      } catch {
        if (!cancelled) {
          setSectionLabel(defaultSectionLabel);
          setTitle(defaultTitle);
          setTableOneTitle(defaultTableOneTitle);
          setTableTwoTitle(defaultTableTwoTitle);
          setRemote(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [strapiApiId, defaultSectionLabel, defaultTitle, defaultTableOneTitle, defaultTableTwoTitle]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '40vh', alignItems: 'center' }}>
          <CircularProgress sx={{ color: '#1398aa' }} />
        </Box>
        <Footer />
      </div>
    );
  }

  const bodyTop = normalizeRich(remote?.bodyTop);
  const bodyBottom = normalizeRich(remote?.bodyBottom);
  const tableOneColumns = normalizeTableColumns(remote?.tableOneColumns);
  const tableOneRows = normalizeTableRows(remote?.tableOneRows);
  const tableTwoColumns = normalizeTableColumns(remote?.tableTwoColumns);
  const tableTwoRows = normalizeTableRows(remote?.tableTwoRows);

  const hasTableOne = hasStructuredTable(tableOneColumns, tableOneRows);
  const hasTableTwo = hasStructuredTable(tableTwoColumns, tableTwoRows);
  const useCms = Boolean(bodyTop || hasTableOne || hasTableTwo || bodyBottom);

  return (
    <div>
      <Navbar />
      <Fade triggerOnce={fadeTriggerOnce}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '800', fontSize: '1.2rem', marginBottom: '2rem', color: '#ae39ed', textAlign: 'center' }}>
            {sectionLabel}
          </h1>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: '700', fontSize: '48px', textAlign: 'center' }}>{title}</h1>
          <img src={hr} alt="" width="80" height="50" />
        </div>
        {useCms ? (
          <Container maxWidth="md" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
            {bodyTop ? <StrapiRichText source={bodyTop} sx={richTextSx} /> : null}
            {hasTableOne ? (
              <>
                <h2 style={{ fontFamily: 'Montserrat', lineHeight: 1.8, fontSize: '30px', marginBottom: '3rem', fontWeight: 700, textAlign: 'center' }}>
                  {tableOneTitle}
                </h2>
                {renderSalaryTable(tableOneColumns, tableOneRows)}
              </>
            ) : null}
            {hasTableTwo ? (
              <>
                <h2 style={{ fontFamily: 'Montserrat', lineHeight: 1.8, fontSize: '30px', marginBottom: '3rem', fontWeight: 700, textAlign: 'center' }}>
                  {tableTwoTitle}
                </h2>
                {renderSalaryTable(tableTwoColumns, tableTwoRows)}
              </>
            ) : null}
            {bodyBottom ? <StrapiRichText source={bodyBottom} sx={richTextSx} /> : null}
          </Container>
        ) : (
          <StaticFallback />
        )}
        <Footer />
      </Fade>
    </div>
  );
}
